import { useAsyncEffect } from 'ahooks';
import { Button, Menu, Modal, Popover } from 'antd';
import React, { Fragment, useState } from 'react';
import { ReportGuideType } from '@/config/app/auth';
import { DATONG_ELEMENT_PARAMS, DATONG_PAGE_EID } from '@/infrastructure/reporter/datong/constant';
import { ReportField, ReportScene } from '@/infrastructure/reporter/datong/report';
import { MatchMode } from '@/presentation/components/form/standards/types';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import { IssueStatusBatch } from '@/presentation/components/issue-status-batch';
import { BatchTrend } from '@/presentation/components/issue-table/batch-trend';
import {
  IssueTagBatch,
  IssueTapdHandlerBatch,
  IssueTapdStatusBatch,
} from '@/presentation/components/issue-tapd-enable-batch';
import { TagBaseType, TagSubType } from '@/presentation/components/question-tag/api';
import { BatchCreateTapd, BatchCreateTapdProps } from '@/presentation/components/tapd/batch-create/batch-create-tapd';
import { useStores } from '@/presentation/store';
import { Zone } from '@/proto/field';
import { IssueTimeType } from '@/proto/issue';
import { MetricType } from '@/proto/metric';
import { genStandardToken, parseStandardToken } from '@/proto/token';
import styles from './index.module.scss';

export type BatchOperateProps = BatchCreateTapdProps<any> & {
  metricType?: MetricType;
  searchParams?: Record<string, any>;
  onExport?: () => void;
  onCancel?: () => void;
  exportLoading?: boolean;
  options?: { label: string, value: string }[];
  disabled?: boolean;
  enableBatchCreateTapd?: boolean;
  enableBatchExport?: boolean;
  enableBatchCancel?: boolean;
  enableBatchStatus?: boolean;
  enableBatchTapdHandler?: boolean;
  enableBatchTag?: {
    baseType: TagBaseType,
    subType: TagSubType,
  };
  enableBatchTapdStatus?: boolean;
  enableBatchTrend?: boolean;
  tapdType?: Zone; // 用批量修改处理状态、处理人，需要和单次对齐
  headToken?: string;
  timeWindow?: IssueTimeType;
  queryCompareToken?: (params: Record<string, any>) => Promise<string>;
};

export const BatchOperate = (props: BatchOperateProps) => {
  const {
    enableBatchCancel,
    enableBatchExport,
    enableBatchCreateTapd,
    enableBatchStatus,
    enableBatchTapdHandler,
    enableBatchTag,
    enableBatchTapdStatus,
    rows,
    tapdType,
    enableBatchTrend = true,
    queryCompareToken,
    timeWindow,
    metricType,
  } = props;
  const { isFreeProduct } = useStores().ProductStore;
  const isFree = isFreeProduct();
  const [visible, setVisible] = useState(false);
  const [trendVisible, setTrendVisible] = useState(false);
  const [trendToken, setTrendToken] = useState('');

  const onVisibleChange = (v: boolean) => {
    if (props.disabled) {
      return;
    }
    setVisible(v);
  };

  const showTrend = () => {
    setTrendVisible(true);
  };

  useAsyncEffect(async () => {
    if (trendVisible) {
      const token = props.headToken;
      if (token) {
        const searchParams = await parseStandardToken(token);
        const filters = searchParams.filters ? [...searchParams.filters] : [];
        // 拼issueID filters参数
        const issueIDs = props.rows.map(item => item.issueId || item.feature);
        const issueIDFilterIndex = filters.findIndex(item => item.name === 'issue_id');

        if (issueIDFilterIndex !== -1) {
          filters[issueIDFilterIndex] = {
            name,
            operator: MatchMode.OR,
            values: issueIDs,
          };
        } else {
          filters.push({
            name: metricType === MetricType.TYPE_RUM_JS ? 'feature' : 'issue_uuid',
            operator: MatchMode.OR,
            values: issueIDs,
          });
        }

        searchParams.filters = filters;
        searchParams.standardIssueId = {
          mode: MatchMode.OR,
          values: issueIDs,
        };
        const trendToken = await genStandardToken(searchParams);
        setTrendToken(trendToken);
      }
    }
  }, [trendVisible]);

  const menu = (
    <Menu selectable={false} onClick={() => {
      setVisible(false);
    }}>
      {enableBatchStatus && <Menu.Item disabled={props.rows?.length === 0} key={'issueStatusBatch'}>
        <IssueStatusBatch
          type={props.type}
          rows={props.rows}
          onSubmit={props.onSubmit}
          options={props.options}
        />
      </Menu.Item>}
      {enableBatchCreateTapd && <Menu.Item disabled={props.rows?.length === 0 || isFree} key={'batchCreateTapd'}>
        <BatchCreateTapd
          issueType={props.issueType}
          type={props.type}
          rows={props.rows}
          onSubmit={props.onSubmit}
          getIssueUrl={props.getIssueUrl}
          pageId={props.pageId}
          trigger={<div>提单</div>}
        />
      </Menu.Item>}
      {enableBatchCancel && <Menu.Item key={'cancel'}>
        <div onClick={props.onCancel}>取消关联</div>
      </Menu.Item>}
      {enableBatchTapdHandler ? <Menu.Item key={'tapdHandler'}>
        <IssueTapdHandlerBatch type={tapdType || props.type}
                               rows={props.rows}
                               onSubmit={props.onSubmit}/>
      </Menu.Item> : null}
      {enableBatchTapdStatus ? <Menu.Item key={'tapdStatus'}>
        <IssueTapdStatusBatch
          type={tapdType || props.type}
          rows={props.rows}
          onSubmit={props.onSubmit}/>
      </Menu.Item>  : null}
      {enableBatchTag ? <Menu.Item key={'tag'} disabled={isFree}>
        <IssueTagBatch rows={rows} enableBatchTag={enableBatchTag}  onSubmit={props.onSubmit}/>
      </Menu.Item> : null}
      {enableBatchTrend && <Menu.Item key={'trend'} disabled={isFree}>
        <FreeProductGuide className={'text-[#d9d9d9]'} guideType={GuideType.POP_CONFIRM} display={Display.BLOCK}
          reportGuideType={ReportGuideType.Bubble}
          reportScene={ReportScene.showTrend}
          reportField={ReportField.showTrend}>
          <div onClick={showTrend}>查看上报趋势</div>
        </FreeProductGuide>
      </Menu.Item>}
      {enableBatchCancel && <Menu.Item key={'cancel'} disabled={isFree}>
        <FreeProductGuide guideType={GuideType.POP_CONFIRM} display={Display.BLOCK}>
          <div onClick={props.onCancel}>取消关联</div>
        </FreeProductGuide>
      </Menu.Item>}
      {enableBatchExport && <Menu.Item key={'exportExcel'} disabled={isFree}>
        <FreeProductGuide className={'text-[#d9d9d9]'} guideType={GuideType.POP_CONFIRM} display={Display.BLOCK}>
          <div
            dt-eid={DATONG_PAGE_EID.issueListExport}
            dt-params={`${DATONG_ELEMENT_PARAMS.issueListBtnName}=导出excel`}
            onClick={() => {
              props.onExport?.();
            }}
          >导出数据</div>
        </FreeProductGuide>
      </Menu.Item>}
      {enableBatchTag ? <Menu.Item key={'tag'}>
        <IssueTagBatch rows={rows} enableBatchTag={enableBatchTag}  onSubmit={props.onSubmit}/>
      </Menu.Item> : null}
    </Menu>
  );


  return (
    <Fragment>
      <Popover
        content={menu}
        trigger={'click'}
        overlayClassName={styles.operatePop}
        visible={visible}
        onVisibleChange={onVisibleChange}
      >
        <Button className={styles.indexBtn} disabled={props.disabled}>批量操作</Button>
      </Popover>

      <Modal
        title={'查看趋势'}
        visible={trendVisible}
        width={730}
        onCancel={() => setTrendVisible(false)}
        footer={false}
        destroyOnClose={true}
      >
        <BatchTrend
          headToken={trendToken}
          searchParams={props.searchParams}
          endDateStr={props.searchParams?.endDateStr}
          startDateStr={props.searchParams?.startDateStr}
          launchDuration={props.searchParams?.launchDuration}
          issueType={props.issueType}
          metricType={props.metricType as MetricType}
          token={trendToken}
          timeWindow={props.searchParams?.timeWindow || timeWindow}
          pgId={props.pageId}
          queryCompareToken={queryCompareToken}
        />
      </Modal>
    </Fragment>
  );
};
